// React
import { useState } from 'react'

// Mantine
import { Anchor, Badge, Button, Divider } from '@mantine/core'

// Dayjs
import dayjs from 'dayjs'

// Next
import { Link } from 'react-router-dom'
// Icons
import { IconExternalLink } from '@tabler/icons-react'

// UI
import OrderStatusBadges, {
    isOrderSettled,
    SHOPIFY_CANCEL_REASONS,
} from './OrderStatusBadges'
import OrderWarrantiesDisplay from './OrderWarrantiesDisplay'
import FulfillmentsDisplay from '../fulfillments/FulfillmentsDisplay'
import Banner from '../ui/Banner'
import Box from '../ui/Box'

// API
import { useAxiosApi } from '@/hooks/useAxiosApi'

import { CANCELLATION_REASONS } from '@/lib/cancellationReasons'

export default function OrderDisplay({ order, reload }) {
    const { requestPost } = useAxiosApi()
    const [isResolving, setIsResolving] = useState(false)

    // Une demande tranchée ne bloque plus rien : ni la file de préparation, ni
    // la page de suivi. Les colonnes, elles, gardent la trace de la demande.
    const cancellationPending =
        order.cancellation_requested_at &&
        !order.cancellation_resolved_at &&
        !isOrderSettled(order)

    const resolveCancellation = async () => {
        setIsResolving(true)

        try {
            await requestPost(`/orders/${order.id}/cancellation/resolve`)
            await reload?.()
        } finally {
            setIsResolving(false)
        }
    }

    return (
        <div className="flex flex-col gap-5">
            {order.cancelled_at && (
                <Banner compact type="error" title="Commande annulée">
                    Annulée le{' '}
                    {dayjs(order.cancelled_at).format('LL [à] HH:mm')}
                    {order.cancel_reason &&
                        ` — motif : ${
                            SHOPIFY_CANCEL_REASONS[order.cancel_reason] ??
                            order.cancel_reason
                        }`}
                </Banner>
            )}

            {cancellationPending && (
                <Banner
                    compact
                    type="warning"
                    title="Annulation demandée par le client"
                >
                    <div className="flex items-center justify-between gap-3">
                        <div className="flex flex-col gap-1">
                            <span>
                                Demande reçue le{' '}
                                {dayjs(order.cancellation_requested_at).format(
                                    'LL [à] HH:mm'
                                )}
                                . Les préparations de cette commande sont
                                suspendues.
                            </span>

                            {order.cancellation_reason && (
                                <span>
                                    Motif :{' '}
                                    {CANCELLATION_REASONS[
                                        order.cancellation_reason
                                    ] ?? order.cancellation_reason}
                                </span>
                            )}
                        </div>

                        <div className="flex items-center gap-2 shrink-0">
                            {/* Le fil ouvert au nom du client à la demande :
                                c'est là qu'on lui répond. */}
                            {order.cancellation_ticket_id && (
                                <Button
                                    component="a"
                                    target="_blank"
                                    color="orange"
                                    variant="subtle"
                                    size="compact-sm"
                                    rightSection={
                                        <IconExternalLink size={14} />
                                    }
                                    href={`https://balticwatches.gorgias.com/app/ticket/${order.cancellation_ticket_id}`}
                                >
                                    Voir le ticket
                                </Button>
                            )}

                            {/* Lever la demande ne l'efface pas : la
                                préparation repart simplement dans la file.
                                Annuler réellement la commande se fait depuis
                                Shopify — ce bouton est là pour le refus. */}
                            <Button
                                color="orange"
                                variant="light"
                                size="compact-sm"
                                loading={isResolving}
                                onClick={resolveCancellation}
                            >
                                Lever la demande
                            </Button>
                        </div>
                    </div>
                </Banner>
            )}

            <Box
                title={`Commande ${order.name}`}
                subtitle={dayjs(order.created_at).format('LL [à] HH:mm')}
                rightSection={
                    <div className="flex items-center gap-3 shrink-0">
                        <OrderStatusBadges order={order} />

                        <Anchor
                            target="_blank"
                            href={
                                'https://baltic-watches.com/order/' +
                                order.id +
                                '/' +
                                order.order_hash?.slice(0, 16)
                            }
                            size="sm"
                        >
                            Suivi commande
                        </Anchor>

                        <Anchor
                            target="_blank"
                            href={
                                'https://admin.shopify.com/store/baltic-shfy/orders/' +
                                order.id
                            }
                            size="sm"
                        >
                            <span className="inline-flex items-center gap-1">
                                Shopify <IconExternalLink size={14} />
                            </span>
                        </Anchor>
                    </div>
                }
            >
                <div className="flex gap-8">
                    <div className="flex flex-col flex-1">
                        <span className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-2">
                            Client
                        </span>
                        <span className="font-medium">
                            {order.customer.first_name}{' '}
                            {order.customer.last_name}
                        </span>
                        <span className="text-gray-600">
                            {order.customer.email}
                        </span>
                    </div>

                    <Divider orientation="vertical" />

                    <div className="flex flex-col flex-1">
                        <span className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-2">
                            Destinataire
                        </span>
                        <span className="font-medium">
                            {order.shipping_address.first_name}{' '}
                            {order.shipping_address.last_name}
                        </span>
                        <span className="text-gray-600">
                            {order.shipping_address.address1}
                        </span>
                        {order.shipping_address.address2 && (
                            <span className="text-gray-600">
                                {order.shipping_address.address2}
                            </span>
                        )}
                        <span className="text-gray-600">
                            {order.shipping_address.zip}{' '}
                            {order.shipping_address.city}
                        </span>
                        <span className="text-gray-600">
                            {order.shipping_address.country_code}
                        </span>
                    </div>
                </div>

                {order.tags && (
                    <div className="flex gap-1 flex-wrap">
                        {order.tags.split(',').map((tag) => (
                            <Badge key={tag} variant="light" color="gray">
                                {tag.trim()}
                            </Badge>
                        ))}
                    </div>
                )}
            </Box>

            <OrderWarrantiesDisplay warranties={order.warranties} />

            <Box title="Demandes de préparation">
                <div className="flex flex-col border rounded-xl overflow-hidden divide-y">
                    {order.fulfillment_orders
                        .sort((a, b) => a.id - b.id)
                        .map((fulfillment_order) => (
                            <Link
                                key={fulfillment_order.id}
                                to={
                                    '/fulfillment_orders/' +
                                    fulfillment_order.id
                                }
                                className="flex items-center gap-3 p-3 even:bg-gray-50 hover:bg-gray-50/80 transition-colors no-underline text-inherit"
                            >
                                <div className="flex flex-col gap-0.5">
                                    <span className="text-sm font-medium text-blue-600">
                                        BP{fulfillment_order.id}
                                    </span>

                                    <span className="text-xs text-gray-400">
                                        {fulfillment_order.location.name}
                                    </span>
                                </div>

                                <div className="ml-auto flex items-center gap-3">
                                    <span className="text-sm text-gray-500">
                                        {fulfillment_order.fulfill_at
                                            ? dayjs(
                                                  fulfillment_order.fulfill_at
                                              ).format('ll')
                                            : 'Pas de date'}
                                    </span>

                                    <span
                                        className={`text-xs font-medium px-2 py-0.5 rounded-full ${fulfillment_order.status === 'OPEN' ? 'bg-green-50 text-green-600' : 'bg-gray-100 text-gray-500'}`}
                                    >
                                        {fulfillment_order.status === 'OPEN'
                                            ? 'Ouverte'
                                            : 'Fermée'}
                                    </span>
                                </div>
                            </Link>
                        ))}
                </div>
            </Box>

            <FulfillmentsDisplay fulfillments={order.fulfillments} />
        </div>
    )
}
